import {
  Box,
  Collapse,
  Grid2,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { ReactElement, useEffect, useState } from "react";
import {
  checkBox,
  collapsibleIcon,
  collapsibleIconCont,
  flexRowCont,
  moduleItem,
  privilege,
} from "../sxprops";
import { Module } from "../api/types";
import { CheckOutlined, PlayArrowRounded } from "@mui/icons-material";
import { AppSwitch } from "../../../../common/components/app-switch";

interface IModuleItem {
  disabled?: boolean;
  walkthroughId?: string;
  walkthroughExpanded?: boolean;
  item: Module;
  isSelected: boolean;
  prefixIcon: ReactElement;
  onSelectModule: (moduleId: string) => void;
  onSelectPrivilege: (moduleId: string, privilegeId: string) => void;
  onAllowFullAccess: (moduleId: string) => void;
}

export const ModuleItem: React.FC<IModuleItem> = ({
  disabled = false,
  walkthroughId,
  walkthroughExpanded,
  item,
  isSelected,
  prefixIcon,
  onSelectModule,
  onSelectPrivilege,
  onAllowFullAccess,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(false);

  useEffect(() => {
    if (walkthroughExpanded === true) setCollapsed(true);
    else if (walkthroughExpanded === false) setCollapsed(false);
  }, [walkthroughExpanded]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const titleSize = { mobile: isMobile ? 12 : 4 };
  const captionSize = { mobile: isMobile ? 12 : 6 };
  const hasPrivilege = item.privileges.length > 0;
  const areAllPrivilegesEnabled = item.privileges.every(
    (privilege) => privilege.enable === true
  );

  return (
    <Box
      id={walkthroughId}
      sx={{
        ...moduleItem,
      }}
    >
      <Collapse in={collapsed && hasPrivilege} collapsedSize={67}>
        <Box
          sx={{
            ...moduleItem.header,
          }}
        >
          <Box
            id={`checkbox-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => {
              if (!disabled) {
                onSelectModule(item.id);
                setCollapsed(!isSelected);
              } else {
                setCollapsed(!collapsed);
              }
            }}
            sx={checkBox(disabled, isSelected)}
          >
            {
              <CheckOutlined
                fontSize={"small"}
                sx={{
                  visibility: isSelected ? "visible" : "hidden",
                }}
              />
            }
          </Box>
          <Grid2
            container
            sx={{
              ...moduleItem.header.title,
            }}
          >
            <Grid2 sx={{ ...flexRowCont, maxWidth: "350px" }} size={titleSize}>
              {prefixIcon}
              <Typography
                variant="body2"
                fontWeight={600}
                color={collapsed ? "primary.main" : "text.secondary"}
                paddingLeft={1}
                textAlign={"start"}
              >
                {item.name}
              </Typography>
            </Grid2>

            <Grid2 sx={flexRowCont} size={captionSize}>
              <Typography
                variant="caption"
                color={collapsed ? "primary.main" : "text.secondary"}
                textAlign={"start"}
                sx={{
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: collapsed ? 4 : 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {item.caption}
              </Typography>
            </Grid2>
          </Grid2>

          <Box
            sx={{
              ...flexRowCont,
            }}
          >
            {collapsed && hasPrivilege && (
              <Box
                sx={{
                  ...flexRowCont,
                  minWidth: isMobile ? null : "150px",
                }}
              >
                <Tooltip title={isMobile ? "Full Access" : ""}>
                  <AppSwitch
                    id={`switch-${item.name.toLowerCase().replace(/\s+/g, "-")}-full-access`}
                    disabled={!item.enable}
                    checked={areAllPrivilegesEnabled}
                    onChange={(event) => {
                      onAllowFullAccess(item.id);
                    }}
                    sx={{
                      mt: "5px",
                    }}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Tooltip>
                {!isMobile && (
                  <Typography color="primary.light" variant="body2">
                    Full Access
                  </Typography>
                )}
              </Box>
            )}

            {hasPrivilege && (
              <Box
                         id={`dropdown-arrow-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                sx={collapsibleIconCont}
                onClick={() => {
                  setCollapsed(!collapsed);
                }}
              >
                <PlayArrowRounded
                  fontSize="large"
                  sx={collapsibleIcon(collapsed)}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/**PRIVILEGES */}
        <Box sx={privilege}>
          {Object.values(item.privileges).map((priv, i) => {
            const isLastItem = i === item.privileges.length - 1;

            return (
              <Grid2
                key={i}
                container
                sx={{
                  mx: "20px",
                  borderBottom: isLastItem ? "none" : "1px solid",
                  borderBottomColor: "text.secondary",
                }}
              >
                <Grid2
                  size={{
                    mobile: isMobile ? 3 : 1,
                  }}
                >
                  <AppSwitch
                    id={`switch-${item.name.toLowerCase().replace(/\s+/g, "-")}-${i}`}
                    value={priv.enable}
                    disabled={!item.enable}
                    checked={priv.enable}
                    onChange={(event) => {
                      onSelectPrivilege(item.id, priv.id);
                    }}
                    sx={{
                      mt: "5px",
                    }}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Grid2>

                <Grid2
                  sx={flexRowCont}
                  size={{
                    mobile: isMobile ? 9 : 2.9,
                  }}
                >
                  <Typography
                    textAlign={"start"}
                    variant="caption"
                    color={priv.enable ? "primary.main" : "text.secondary"}
                    fontWeight={500}
                  >
                    {priv.name}
                  </Typography>
                </Grid2>

                <Grid2
                  sx={{
                    ...flexRowCont,
                    visibility: "hidden",
                  }}
                  size={{
                    mobile: isMobile ? 3 : 0,
                  }}
                >
                  -
                </Grid2>

                <Grid2
                  sx={flexRowCont}
                  size={{
                    mobile: isMobile ? 9 : 5,
                  }}
                >
                  <Typography
                    textAlign={"start"}
                    variant="caption"
                    color={priv.enable ? "primary.main" : "text.secondary"}
                  >
                    {priv.caption}
                  </Typography>
                </Grid2>
              </Grid2>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};
